/**
 * Map-specific IndexedDB store configuration.
 * Registers map data stores with the global database without polluting the shared db.js.
 *
 * IMPORTANT - Class Instance Reconstruction:
 * IndexedDB stores all objects as plain JSON, which means class instances lose their methods
 * when stored and retrieved. This file includes reconstruction logic in getter functions to
 * restore class instances (CampusData, DatasourceColumn, etc.) so that their methods remain
 * available after retrieval. Without this, code like `campusData.getBuildingById()` would fail.
 */
import { registerStoreUpgrade, getRecord, putRecord, deleteRecord } from "@ciq/shared/db/db"
import { CampusData } from "../models/CampusData"
import { DatasourceColumn } from "../models/DatasourceColumn"

// Map store names
export const MAP_STORES = {
  DATASOURCES: "map_datasources",
  RESOURCES: "map_resources",
  SEMESTERS: "map_semesters",
  HOURLY: "map_hourly",
  CACHE_METADATA: "cache_metadata",
}

/**
 * Registers map-specific stores with the database.
 * This function should be called early in the application lifecycle (e.g., at module load time).
 */
export const initMapStores = () => {
  registerStoreUpgrade((db, event) => {
    const oldVersion = event.oldVersion

    // Create map datasources store if it doesn't exist
    if (!db.objectStoreNames.contains(MAP_STORES.DATASOURCES)) {
      db.createObjectStore(MAP_STORES.DATASOURCES, { keyPath: "schoolId" })
    }

    // Create map resources store if it doesn't exist
    if (!db.objectStoreNames.contains(MAP_STORES.RESOURCES)) {
      db.createObjectStore(MAP_STORES.RESOURCES, { keyPath: "schoolId" })
    }

    // Create map semesters store if it doesn't exist
    if (!db.objectStoreNames.contains(MAP_STORES.SEMESTERS)) {
      const semesterStore = db.createObjectStore(MAP_STORES.SEMESTERS, {
        keyPath: ["schoolId", "semesterId"],
      })
      semesterStore.createIndex("schoolId", "schoolId", { unique: false })
    }

    // Create map hourly store if it doesn't exist
    if (!db.objectStoreNames.contains(MAP_STORES.HOURLY)) {
      const hourlyStore = db.createObjectStore(MAP_STORES.HOURLY, {
        keyPath: ["schoolId", "semesterId"],
      })
      hourlyStore.createIndex("schoolId", "schoolId", { unique: false })
    }

    // Create cache metadata store if it doesn't exist
    if (!db.objectStoreNames.contains(MAP_STORES.CACHE_METADATA)) {
      db.createObjectStore(MAP_STORES.CACHE_METADATA, { keyPath: "storeKey" })
    } else if (oldVersion <= 2) {
      // Migration: Clear cache_metadata when upgrading from v2 to v3+
      // v2 schema: {cachedAt, lastFetchAt}
      // v3 schema: {backendFileModifiedAt, cachedFileModifiedAt}
      // Old records incompatible with new timestamp comparison logic
      const transaction = event.target.transaction
      const store = transaction.objectStore(MAP_STORES.CACHE_METADATA)
      store.clear()
      console.info("[IndexedDB Migration] Cleared cache_metadata for schema v3 upgrade")
    }

    if (oldVersion < 4 && event.target?.transaction) {
      // Migration: Clear datasources cache when upgrading from pre-v4 to v4
      // Pre-v4 datasources missing colorRanges field (ENG-2100)
      // Old records incompatible with new API contract requiring colorRanges
      const transaction = event.target.transaction
      if (transaction.objectStoreNames.contains(MAP_STORES.DATASOURCES)) {
        const store = transaction.objectStore(MAP_STORES.DATASOURCES)
        store.clear()
        console.info("[IndexedDB Migration] Cleared datasources for v4 upgrade (colorRanges support)")
      }
      if (transaction.objectStoreNames.contains(MAP_STORES.CACHE_METADATA)) {
        const metaStore = transaction.objectStore(MAP_STORES.CACHE_METADATA)
        metaStore.clear()
        console.info("[IndexedDB Migration] Cleared cache_metadata for v4 upgrade")
      }
    }
  })
}

// Initialize stores at module load time
initMapStores()

// ========== Cache Metadata Functions ==========

/**
 * Generates a unique cache key for a store entry.
 * Format: "map_{storeName}:{schoolId}" or "map_{storeName}:{schoolId}:{semesterId}"
 *
 * @param {string} storeName - The store name (from MAP_STORES)
 * @param {string} schoolId - The unique identifier for the school
 * @param {string|null} semesterId - Optional semester identifier for semester-specific stores
 * @returns {string} The cache key
 *
 * @example
 * generateCacheKey(MAP_STORES.DATASOURCES, "school-123")  // "map_datasources:school-123"
 * generateCacheKey(MAP_STORES.SEMESTERS, "school-123", "sem-456")  // "map_semesters:school-123:sem-456"
 */
export const generateCacheKey = (storeName, schoolId, semesterId = null) => {
  if (semesterId) {
    return `${storeName}:${schoolId}:${semesterId}`
  }
  return `${storeName}:${schoolId}`
}

/**
 * Retrieves cache metadata for a specific store key.
 *
 * @param {string} storeKey - The cache key
 * @returns {Promise<Object|null>} Cache metadata with cachedAt timestamp, or null if not found
 */
export const getCacheMetadata = async storeKey => {
  return await getRecord(MAP_STORES.CACHE_METADATA, storeKey)
}

/**
 * Records or updates cache metadata with file modification timestamps.
 * Merges provided updates with existing metadata - only overwrites fields explicitly passed.
 *
 * @param {string} storeKey - The cache key
 * @param {Object} updates - Fields to update
 * @param {number} [updates.backendFileModifiedAt] - Unix timestamp from backend (seconds since epoch)
 * @param {number} [updates.cachedFileModifiedAt] - Unix timestamp when we fetched this version
 * @returns {Promise<void>}
 */
export const setCacheMetadata = async (storeKey, updates = {}) => {
  const existing = await getCacheMetadata(storeKey) || {}
  await putRecord(MAP_STORES.CACHE_METADATA, {
    ...existing,
    storeKey,
    ...updates  // Only overwrite fields explicitly passed
  })
}

/**
 * Clears cache metadata for a specific store key.
 *
 * @param {string} storeKey - The cache key
 * @returns {Promise<void>}
 */
const clearCacheMetadata = async storeKey => {
  await deleteRecord(MAP_STORES.CACHE_METADATA, storeKey)
}

/**
 * Checks if cached data is stale based on file modification timestamps.
 * Compares backend timestamp (from GeoAPI via landing) with cached timestamp (set when data stored).
 *
 * @param {string} storeKey - The cache key
 * @returns {Promise<boolean>} True if cache is stale
 */
const isCacheStale = async storeKey => {
  const metadata = await getCacheMetadata(storeKey)

  // 1. No metadata? Never cached, need to fetch
  if (!metadata) return true

  // 2. No backend timestamp? No file exists in S3, nothing to fetch
  // Landing writes backendFileModifiedAt from Redis; missing means no GeoAPI file
  if (!metadata.backendFileModifiedAt) return false

  // 3. No cached timestamp? Have metadata but never fetched, need to fetch
  if (!metadata.cachedFileModifiedAt) return true

  // 4. Compare timestamps - stale if they differ
  return metadata.cachedFileModifiedAt !== metadata.backendFileModifiedAt
}

// ========== Datasource Data Functions ==========

/**
 * Checks if datasource data exists in IndexedDB.
 *
 * @param {string} schoolId - The unique identifier for the school
 * @returns {Promise<boolean>} A promise that resolves to true if datasource data exists
 */
export const hasDatasourceData = async schoolId => {
  const record = await getRecord(MAP_STORES.DATASOURCES, schoolId)
  return record != null
}

/**
 * Retrieves datasource data from IndexedDB and reconstructs DatasourceColumn instances.
 *
 * IMPORTANT: Reconstructs all nested DatasourceColumn instances to restore class methods.
 * IndexedDB stores objects as plain JSON, so class instances must be explicitly reconstructed
 * for datasets, hourlyTable, and semesterTable column arrays.
 *
 * @param {string} schoolId - The unique identifier for the school
 * @returns {Promise<Object|null>} A promise that resolves to datasource data with reconstructed column instances, or null if not found
 */
export const getDatasourceData = async schoolId => {
  const record = await getRecord(MAP_STORES.DATASOURCES, schoolId)
  if (record == null) {
    return null
  }

  // Reconstruct all DatasourceColumn instances to restore class methods
  return {
    ...record.data,
    datasets: record.data.datasets.map(dataset => ({
      ...dataset,
      columns: dataset.columns.map(DatasourceColumn.fromRecord),
    })),
    hourlyTable: {
      ...record.data.hourlyTable,
      columns: record.data.hourlyTable.columns.map(DatasourceColumn.fromRecord),
      enabledColumns: record.data.hourlyTable.enabledColumns.map(DatasourceColumn.fromRecord),
      mapEnabledColumns: record.data.hourlyTable.mapEnabledColumns.map(DatasourceColumn.fromRecord),
    },
    semesterTable: {
      ...record.data.semesterTable,
      columns: record.data.semesterTable.columns.map(DatasourceColumn.fromRecord),
      enabledColumns: record.data.semesterTable.enabledColumns.map(DatasourceColumn.fromRecord),
      mapEnabledColumns: record.data.semesterTable.mapEnabledColumns.map(
        DatasourceColumn.fromRecord
      ),
    },
  }
}

/**
 * Stores datasource configuration data in IndexedDB and records cache timestamp.
 * Marks the cache as fresh by setting cachedFileModifiedAt to match backendFileModifiedAt.
 *
 * @param {string} schoolId - The unique identifier for the school
 * @param {any} data - Datasource configuration data to store
 * @returns {Promise<void>} A promise that resolves when data is stored
 */
export const setDatasourceData = async (schoolId, data) => {
  await putRecord(MAP_STORES.DATASOURCES, {
    schoolId,
    data,
  })
  const cacheKey = generateCacheKey(MAP_STORES.DATASOURCES, schoolId)

  // Get backend timestamp and mark as cached (setCacheMetadata merges, preserving other fields)
  const metadata = await getCacheMetadata(cacheKey)
  const backendTimestamp = metadata?.backendFileModifiedAt

  await setCacheMetadata(cacheKey, {
    cachedFileModifiedAt: backendTimestamp,
  })
}

/**
 * Removes datasource configuration data and its cache metadata from IndexedDB.
 *
 * @param {string} schoolId - The unique identifier for the school
 * @returns {Promise<void>} A promise that resolves when data is removed
 */
export const deleteDatasourceData = async schoolId => {
  await deleteRecord(MAP_STORES.DATASOURCES, schoolId)
  const cacheKey = generateCacheKey(MAP_STORES.DATASOURCES, schoolId)
  await clearCacheMetadata(cacheKey)
}

// ========== Resource Data Functions ==========

/**
 * Checks if resource data exists in IndexedDB.
 *
 * @param {string} schoolId - The unique identifier for the school
 * @returns {Promise<boolean>} A promise that resolves to true if resource data exists
 */
export const hasResourceData = async schoolId => {
  const record = await getRecord(MAP_STORES.RESOURCES, schoolId)
  return record != null
}

/**
 * Retrieves resource data from IndexedDB and reconstructs it as a CampusData instance.
 *
 * IMPORTANT: Returns a CampusData class instance, not a plain object. This ensures that
 * instance methods (getBuildingById, getZoneById, etc.) are available. IndexedDB stores
 * objects as plain JSON, so class instances must be explicitly reconstructed.
 *
 * @param {string} schoolId - The unique identifier for the school
 * @returns {Promise<CampusData|null>} A promise that resolves to a CampusData instance or null if not found
 */
export const getResourceData = async schoolId => {
  const record = await getRecord(MAP_STORES.RESOURCES, schoolId)
  if (record == null) {
    return null
  }

  // Reconstruct CampusData instance to restore class methods
  return CampusData.fromRecord(record.data)
}

/**
 * Stores resource data in IndexedDB and records cache timestamp.
 * Marks the cache as fresh by setting cachedFileModifiedAt to match backendFileModifiedAt.
 *
 * @param {string} schoolId - The unique identifier for the school
 * @param {CampusData} data - Resource data to store
 * @returns {Promise<void>} A promise that resolves when data is stored
 */
export const setResourceData = async (schoolId, data) => {
  await putRecord(MAP_STORES.RESOURCES, {
    schoolId,
    data,
  })
  const cacheKey = generateCacheKey(MAP_STORES.RESOURCES, schoolId)

  // Get backend timestamp and mark as cached (setCacheMetadata merges, preserving other fields)
  const metadata = await getCacheMetadata(cacheKey)
  const backendTimestamp = metadata?.backendFileModifiedAt

  await setCacheMetadata(cacheKey, {
    cachedFileModifiedAt: backendTimestamp,
  })
}

// ========== Semester Data Functions ==========

/**
 * Checks if semester summary data exists in IndexedDB.
 *
 * @param {string} schoolId - The unique identifier for the school
 * @param {string} semesterId - The unique identifier for the semester
 * @returns {Promise<boolean>} A promise that resolves to true if semester data exists
 */
export const hasSemesterData = async (schoolId, semesterId) => {
  const record = await getRecord(MAP_STORES.SEMESTERS, [schoolId, semesterId])
  return record != null
}

/**
 * Retrieves semester summary data from IndexedDB.
 *
 * @param {string} schoolId - The unique identifier for the school
 * @param {string} semesterId - The unique identifier for the semester
 * @returns {Promise<Object|null>} A promise that resolves to semester data or null if not found
 */
export const getSemesterData = async (schoolId, semesterId) => {
  const record = await getRecord(MAP_STORES.SEMESTERS, [schoolId, semesterId])
  if (record == null) {
    return null
  }

  return record.data
}

/**
 * Stores semester summary data in IndexedDB and records cache timestamp.
 * Marks the cache as fresh by setting cachedFileModifiedAt to match backendFileModifiedAt.
 *
 * @param {string} schoolId - The unique identifier for the school
 * @param {string} semesterId - The unique identifier for the semester
 * @param {any} data - Semester summary data to store
 * @returns {Promise<void>} A promise that resolves when data is stored
 */
export const setSemesterData = async (schoolId, semesterId, data) => {
  await putRecord(MAP_STORES.SEMESTERS, {
    schoolId,
    semesterId,
    data,
  })
  const cacheKey = generateCacheKey(MAP_STORES.SEMESTERS, schoolId, semesterId)

  // Get backend timestamp and mark as cached (setCacheMetadata merges, preserving other fields)
  const metadata = await getCacheMetadata(cacheKey)
  const backendTimestamp = metadata?.backendFileModifiedAt

  await setCacheMetadata(cacheKey, {
    cachedFileModifiedAt: backendTimestamp,
  })
}

// ========== Hourly Data Functions ==========

/**
 * Checks if hourly usage data exists in IndexedDB.
 *
 * @param {string} schoolId - The unique identifier for the school
 * @param {string} semesterId - The unique identifier for the semester
 * @returns {Promise<boolean>} A promise that resolves to true if hourly data exists
 */
export const hasHourlyData = async (schoolId, semesterId) => {
  const record = await getRecord(MAP_STORES.HOURLY, [schoolId, semesterId])
  return record != null
}

/**
 * Retrieves hourly usage data from IndexedDB.
 *
 * @param {string} schoolId - The unique identifier for the school
 * @param {string} semesterId - The unique identifier for the semester
 * @returns {Promise<Object|null>} A promise that resolves to hourly data or null if not found
 */
export const getHourlyData = async (schoolId, semesterId) => {
  const record = await getRecord(MAP_STORES.HOURLY, [schoolId, semesterId])
  if (record == null) {
    return null
  }

  return record.data
}

/**
 * Stores hourly usage data in IndexedDB and records cache timestamp.
 * Marks the cache as fresh by setting cachedFileModifiedAt to match backendFileModifiedAt.
 *
 * @param {string} schoolId - The unique identifier for the school
 * @param {string} semesterId - The unique identifier for the semester
 * @param {any} data - Hourly usage data to store
 * @returns {Promise<void>} A promise that resolves when data is stored
 */
export const setHourlyData = async (schoolId, semesterId, data) => {
  await putRecord(MAP_STORES.HOURLY, {
    schoolId,
    semesterId,
    data,
  })
  const cacheKey = generateCacheKey(MAP_STORES.HOURLY, schoolId, semesterId)

  // Get backend timestamp and mark as cached (setCacheMetadata merges, preserving other fields)
  const metadata = await getCacheMetadata(cacheKey)
  const backendTimestamp = metadata?.backendFileModifiedAt

  await setCacheMetadata(cacheKey, {
    cachedFileModifiedAt: backendTimestamp,
  })
}

// ========== Cache Staleness Check Functions ==========

/**
 * Checks if datasource data is stale based on file modification timestamps.
 * Compares cached file timestamp vs backend file timestamp.
 *
 * @param {string} schoolId - The unique identifier for the school
 * @returns {Promise<boolean>} True if data should be refreshed
 */
export const isDatasourceStale = async (schoolId) => {
  const cacheKey = generateCacheKey(MAP_STORES.DATASOURCES, schoolId)
  const metadata = await getCacheMetadata(cacheKey)

  // No metadata = never cached, need to fetch
  if (!metadata) return true

  // No backend timestamp = no file exists in S3, nothing to fetch
  if (!metadata.backendFileModifiedAt) return false

  // No cached timestamp = have metadata but never fetched, need to fetch
  if (!metadata.cachedFileModifiedAt) return true

  // Compare timestamps - stale if they differ
  return metadata.cachedFileModifiedAt !== metadata.backendFileModifiedAt
}

/**
 * Checks if resource data is stale based on file modification timestamps.
 * Compares cached file timestamp vs backend file timestamp.
 *
 * @param {string} schoolId - The unique identifier for the school
 * @returns {Promise<boolean>} True if data should be refreshed
 */
export const isResourceStale = async (schoolId) => {
  const cacheKey = generateCacheKey(MAP_STORES.RESOURCES, schoolId)
  const metadata = await getCacheMetadata(cacheKey)

  if (!metadata) return true

  // No backend timestamp = no file exists in S3, nothing to fetch
  if (!metadata.backendFileModifiedAt) return false

  if (!metadata.cachedFileModifiedAt) return true

  return metadata.cachedFileModifiedAt !== metadata.backendFileModifiedAt
}

/**
 * Checks if semester summary data is stale based on file modification timestamps.
 * Compares cached file timestamp vs backend file timestamp.
 *
 * @param {string} schoolId - The unique identifier for the school
 * @param {string} semesterId - The unique identifier for the semester
 * @returns {Promise<boolean>} True if data should be refreshed
 */
export const isSemesterStale = async (schoolId, semesterId) => {
  const cacheKey = generateCacheKey(MAP_STORES.SEMESTERS, schoolId, semesterId)
  const metadata = await getCacheMetadata(cacheKey)

  if (!metadata) return true

  // No backend timestamp = no file exists in S3, nothing to fetch
  if (!metadata.backendFileModifiedAt) return false

  if (!metadata.cachedFileModifiedAt) return true

  return metadata.cachedFileModifiedAt !== metadata.backendFileModifiedAt
}

/**
 * Checks if hourly usage data is stale based on file modification timestamps.
 * Compares cached file timestamp vs backend file timestamp.
 *
 * @param {string} schoolId - The unique identifier for the school
 * @param {string} semesterId - The unique identifier for the semester
 * @returns {Promise<boolean>} True if data should be refreshed
 */
export const isHourlyStale = async (schoolId, semesterId) => {
  const cacheKey = generateCacheKey(MAP_STORES.HOURLY, schoolId, semesterId)
  const metadata = await getCacheMetadata(cacheKey)

  if (!metadata) return true

  // No backend timestamp = no file exists in S3, nothing to fetch
  if (!metadata.backendFileModifiedAt) return false

  if (!metadata.cachedFileModifiedAt) return true

  return metadata.cachedFileModifiedAt !== metadata.backendFileModifiedAt
}
